import { z } from "zod"
import { exploreSearchSchema } from "@/features/dashboard/lib/explore-search"
import {
  AdvancedFiltersSchema,
  PINNED_KPI_NAME,
  remapAudienceSegmentValues,
  type AdvancedFilters,
} from "@nexus/contracts"
import { migrateLegacyKpiId } from "@nexus/kpi-formula"
import type {
  DrilldownViewMode,
  DrilldownSortMode,
  DrilldownSortDirection,
} from "../stores/drilldownFilters.store"
import {
  DEFAULT_SEGMENT_1,
  SEGMENT_NONE,
  MY_BRAND_OBJECT_TYPE_OPTIONS,
  MY_BRAND_EFFECTIVE_STATUS_OPTIONS,
} from "../stores/drilldownFilters.store"
import { DEFAULT_MY_BRAND_KPIS } from "./my-brand-kpi-catalog"

const ALL_MY_BRAND_OBJECT_TYPES = MY_BRAND_OBJECT_TYPE_OPTIONS.map((o) => o.value)
const ALL_MY_BRAND_EFFECTIVE_STATUSES = MY_BRAND_EFFECTIVE_STATUS_OPTIONS.map((o) => o.value)

const VIEW_MODE_VALUES = ["grid", "list", "chart"] as const
const SORT_MODE_VALUES = ["Days", "Assets", "Days/Assets", "Date"] as const
const SORT_DIRECTION_VALUES = ["asc", "desc"] as const

export const drilldownSearchSchema = exploreSearchSchema.extend({
  objectType: z.string().optional().catch(undefined),
  effectiveStatus: z.string().optional().catch(undefined),
  // My Brand's own Ad Type/Status selections (different value domain — see
  // MY_BRAND_OBJECT_TYPE_OPTIONS / MY_BRAND_EFFECTIVE_STATUS_OPTIONS in the store).
  myBrandObjectType: z.string().optional().catch(undefined),
  myBrandEffectiveStatus: z.string().optional().catch(undefined),
  startDate: z.string().optional().catch(undefined),
  endDate: z.string().optional().catch(undefined),
  // My Brand's own date range (separate remembered selection — see myBrandStartDate/
  // myBrandEndDate in the store).
  myBrandStartDate: z.string().optional().catch(undefined),
  myBrandEndDate: z.string().optional().catch(undefined),
  segment1: z.string().optional().catch(undefined),
  segment2: z.string().optional().catch(undefined),
  segment3: z.string().optional().catch(undefined),
  // My Brand's own Group By segment selections (same vocabulary, separate remembered
  // selection — see myBrandSegment1/2/3 in the store).
  myBrandSegment1: z.string().optional().catch(undefined),
  myBrandSegment2: z.string().optional().catch(undefined),
  myBrandSegment3: z.string().optional().catch(undefined),
  viewMode: z.enum(VIEW_MODE_VALUES).optional().catch(undefined),
  // Competitors-only fixed sort field (unchanged URL param names for back-compat with
  // bookmarked links). My Brand keeps its own namespaced sort params below, since each
  // mode remembers its own last-used sort (see drilldownFilters.store.ts).
  sortMode: z.enum(SORT_MODE_VALUES).optional().catch(undefined),
  sortDirection: z.enum(SORT_DIRECTION_VALUES).optional().catch(undefined),
  // My Brand's KPI pill row: active sort field id (a MY_BRAND_KPI_CATALOG id — validated
  // against the catalog in drilldownSearchToFilters, not here, since the valid set can
  // grow without a schema change) + direction, and the selected/ordered KPI id list (CSV).
  myBrandSortMode: z.string().optional().catch(undefined),
  myBrandSortDirection: z.enum(SORT_DIRECTION_VALUES).optional().catch(undefined),
  kpis: z.string().optional().catch(undefined),
  // My Brand list-view date columns the user hid (CSV of min_date, max_date, days).
  hiddenColumns: z.string().optional().catch(undefined),
  // JSON-encoded AdvancedFilters array; omitted from URL when empty. Competitors' pair
  // (unchanged param name, back-compat with existing links). My Brand keeps its own
  // namespaced param below — same reasoning as myBrandSortMode/kpis above: the two
  // modes' advanced filters reference different fields and must not leak into each other.
  advancedFilters: z.string().optional().catch(undefined),
  myBrandAdvancedFilters: z.string().optional().catch(undefined),
  audienceSegment: z.string().optional().catch(undefined),
  device: z.string().optional().catch(undefined),
})

export type DrilldownSearch = z.infer<typeof drilldownSearchSchema>

function splitCsv(value: string | undefined): string[] {
  if (!value) return []
  return value.split(",").map((s) => s.trim()).filter(Boolean)
}

function joinCsv(values: string[]): string | undefined {
  if (values.length === 0) return undefined
  return values.join(",")
}

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i])
}

/** Parse a JSON-encoded AdvancedFilters URL param, degrading to [] on any error
 *  (malformed JSON, schema mismatch, or absent param) — never throws. */
function parseAdvancedFilters(raw: string | undefined, paramName: string): AdvancedFilters {
  if (!raw) return []
  try {
    const parsed = AdvancedFiltersSchema.safeParse(JSON.parse(raw))
    if (parsed.success) return parsed.data
    console.warn(`[drilldown] ${paramName} URL param failed schema validation — ignoring`, parsed.error)
    return []
  } catch {
    console.warn(`[drilldown] ${paramName} URL param contains invalid JSON — ignoring`, raw)
    return []
  }
}

export interface DrilldownFilterSnapshot {
  objectType: string[]
  effectiveStatus: string[]
  myBrandObjectType: string[]
  myBrandEffectiveStatus: string[]
  startDate: string
  endDate: string
  myBrandStartDate: string
  myBrandEndDate: string
  segment1: string
  segment2: string
  segment3: string
  myBrandSegment1: string
  myBrandSegment2: string
  myBrandSegment3: string
  viewMode: DrilldownViewMode
  competitorSortMode: DrilldownSortMode
  competitorSortDirection: DrilldownSortDirection
  myBrandKpis: string[]
  myBrandHiddenColumns: string[]
  myBrandSortMode: string
  myBrandSortDirection: DrilldownSortDirection
  advancedFilters: AdvancedFilters
  myBrandAdvancedFilters: AdvancedFilters
  audienceSegment: string[]
  device: string[]
}

/** Deserialize drilldown URL params into store-ready values. */
export function drilldownSearchToFilters(search: DrilldownSearch): DrilldownFilterSnapshot {
  const advancedFilters = parseAdvancedFilters(search.advancedFilters, "advancedFilters")
  const myBrandAdvancedFilters = parseAdvancedFilters(search.myBrandAdvancedFilters, "myBrandAdvancedFilters")
    .map((group) => ({
      ...group,
      rules: group.rules.map((rule) => ({ ...rule, field: migrateLegacyKpiId(rule.field) })),
    }))
  // Migrate legacy camelCase/snake_case ids. Unknown names are kept — the catalog is
  // API-driven, so a custom KPI is not known statically. Empty (`?kpis=`) still falls
  // back to DEFAULT_MY_BRAND_KPIS so the store never lands on [].
  const myBrandKpis = splitCsv(search.kpis).map(migrateLegacyKpiId)
  const myBrandObjectType = splitCsv(search.myBrandObjectType)
  const myBrandEffectiveStatus = splitCsv(search.myBrandEffectiveStatus)
  return {
    objectType: splitCsv(search.objectType),
    effectiveStatus: splitCsv(search.effectiveStatus),
    // Absent param -> full list (same "no filter" meaning as an explicit full selection —
    // see the omit-when-default check in appliedDrilldownToSearch below).
    myBrandObjectType: myBrandObjectType.length > 0 ? myBrandObjectType : ALL_MY_BRAND_OBJECT_TYPES,
    myBrandEffectiveStatus:
      myBrandEffectiveStatus.length > 0 ? myBrandEffectiveStatus : ALL_MY_BRAND_EFFECTIVE_STATUSES,
    startDate: search.startDate ?? "",
    endDate: search.endDate ?? "",
    myBrandStartDate: search.myBrandStartDate ?? "",
    myBrandEndDate: search.myBrandEndDate ?? "",
    segment1: search.segment1 ?? DEFAULT_SEGMENT_1,
    segment2: search.segment2 ?? SEGMENT_NONE,
    segment3: search.segment3 ?? SEGMENT_NONE,
    myBrandSegment1: search.myBrandSegment1 ?? DEFAULT_SEGMENT_1,
    myBrandSegment2: search.myBrandSegment2 ?? SEGMENT_NONE,
    myBrandSegment3: search.myBrandSegment3 ?? SEGMENT_NONE,
    viewMode: search.viewMode ?? "grid",
    competitorSortMode: search.sortMode ?? "Days/Assets",
    competitorSortDirection: search.sortDirection ?? "desc",
    myBrandKpis: myBrandKpis.length > 0 ? myBrandKpis : DEFAULT_MY_BRAND_KPIS,
    myBrandHiddenColumns: splitCsv(search.hiddenColumns),
    myBrandSortMode: search.myBrandSortMode
      ? migrateLegacyKpiId(search.myBrandSortMode)
      : PINNED_KPI_NAME,
    myBrandSortDirection: search.myBrandSortDirection ?? "desc",
    advancedFilters,
    myBrandAdvancedFilters,
    audienceSegment: remapAudienceSegmentValues(splitCsv(search.audienceSegment)),
    device: splitCsv(search.device),
  }
}

/** Serialize applied store state to drilldown URL params (omit defaults to keep URLs clean). */
export function appliedDrilldownToSearch(state: {
  appliedObjectType: string[]
  appliedEffectiveStatus: string[]
  appliedMyBrandObjectType: string[]
  appliedMyBrandEffectiveStatus: string[]
  appliedStartDate: string
  appliedEndDate: string
  appliedMyBrandStartDate: string
  appliedMyBrandEndDate: string
  appliedSegment1: string
  appliedSegment2: string
  appliedSegment3: string
  appliedMyBrandSegment1: string
  appliedMyBrandSegment2: string
  appliedMyBrandSegment3: string
  viewMode: DrilldownViewMode
  competitorSortMode: DrilldownSortMode
  competitorSortDirection: DrilldownSortDirection
  myBrandKpis: string[]
  myBrandHiddenColumns: string[]
  myBrandSortMode: string
  myBrandSortDirection: DrilldownSortDirection
  appliedAdvancedFilters: AdvancedFilters
  appliedMyBrandAdvancedFilters: AdvancedFilters
  appliedAudienceSegment: string[]
  appliedDevice: string[]
}): Partial<DrilldownSearch> {
  return {
    objectType: joinCsv(state.appliedObjectType),
    effectiveStatus: joinCsv(state.appliedEffectiveStatus),
    myBrandObjectType: joinCsv(state.appliedMyBrandObjectType),
    myBrandEffectiveStatus: joinCsv(state.appliedMyBrandEffectiveStatus),
    startDate: state.appliedStartDate || undefined,
    endDate: state.appliedEndDate || undefined,
    myBrandStartDate: state.appliedMyBrandStartDate || undefined,
    myBrandEndDate: state.appliedMyBrandEndDate || undefined,
    segment1: state.appliedSegment1 !== DEFAULT_SEGMENT_1 ? state.appliedSegment1 : undefined,
    segment2: state.appliedSegment2 !== SEGMENT_NONE ? state.appliedSegment2 : undefined,
    segment3: state.appliedSegment3 !== SEGMENT_NONE ? state.appliedSegment3 : undefined,
    myBrandSegment1: state.appliedMyBrandSegment1 !== DEFAULT_SEGMENT_1 ? state.appliedMyBrandSegment1 : undefined,
    myBrandSegment2: state.appliedMyBrandSegment2 !== SEGMENT_NONE ? state.appliedMyBrandSegment2 : undefined,
    myBrandSegment3: state.appliedMyBrandSegment3 !== SEGMENT_NONE ? state.appliedMyBrandSegment3 : undefined,
    viewMode: state.viewMode !== "grid" ? state.viewMode : undefined,
    sortMode: state.competitorSortMode !== "Days/Assets" ? state.competitorSortMode : undefined,
    sortDirection: state.competitorSortDirection !== "desc" ? state.competitorSortDirection : undefined,
    kpis: !arraysEqual(state.myBrandKpis, DEFAULT_MY_BRAND_KPIS) ? joinCsv(state.myBrandKpis) : undefined,
    hiddenColumns: joinCsv(state.myBrandHiddenColumns),
    myBrandSortMode: state.myBrandSortMode !== PINNED_KPI_NAME ? state.myBrandSortMode : undefined,
    myBrandSortDirection: state.myBrandSortDirection !== "desc" ? state.myBrandSortDirection : undefined,
    advancedFilters: state.appliedAdvancedFilters.length > 0
      ? JSON.stringify(state.appliedAdvancedFilters)
      : undefined,
    myBrandAdvancedFilters: state.appliedMyBrandAdvancedFilters.length > 0
      ? JSON.stringify(state.appliedMyBrandAdvancedFilters)
      : undefined,
    audienceSegment: joinCsv(state.appliedAudienceSegment),
    device: joinCsv(state.appliedDevice),
  }
}

/** Extract only the drilldown-specific portion of the search for key tracking. */
export function drilldownSearchKey(search: DrilldownSearch): string {
  return JSON.stringify({
    objectType: search.objectType,
    effectiveStatus: search.effectiveStatus,
    myBrandObjectType: search.myBrandObjectType,
    myBrandEffectiveStatus: search.myBrandEffectiveStatus,
    startDate: search.startDate,
    endDate: search.endDate,
    myBrandStartDate: search.myBrandStartDate,
    myBrandEndDate: search.myBrandEndDate,
    segment1: search.segment1,
    segment2: search.segment2,
    segment3: search.segment3,
    myBrandSegment1: search.myBrandSegment1,
    myBrandSegment2: search.myBrandSegment2,
    myBrandSegment3: search.myBrandSegment3,
    viewMode: search.viewMode,
    sortMode: search.sortMode,
    sortDirection: search.sortDirection,
    kpis: search.kpis,
    hiddenColumns: search.hiddenColumns,
    myBrandSortMode: search.myBrandSortMode,
    myBrandSortDirection: search.myBrandSortDirection,
    advancedFilters: search.advancedFilters,
    myBrandAdvancedFilters: search.myBrandAdvancedFilters,
    audienceSegment: search.audienceSegment,
    device: search.device,
  })
}

/** True if any drilldown-specific param is present in the URL. */
export function hasDrilldownSearchParams(search: DrilldownSearch): boolean {
  return Boolean(
    search.objectType ||
    search.effectiveStatus ||
    search.myBrandObjectType ||
    search.myBrandEffectiveStatus ||
    search.startDate ||
    search.endDate ||
    search.myBrandStartDate ||
    search.myBrandEndDate ||
    search.segment1 ||
    search.segment2 ||
    search.segment3 ||
    search.myBrandSegment1 ||
    search.myBrandSegment2 ||
    search.myBrandSegment3 ||
    search.viewMode ||
    search.sortMode ||
    search.sortDirection ||
    search.kpis ||
    search.hiddenColumns ||
    search.myBrandSortMode ||
    search.myBrandSortDirection ||
    search.advancedFilters ||
    search.myBrandAdvancedFilters ||
    search.audienceSegment ||
    search.device,
  )
}
